import type { FC } from 'react';
import React, { cloneElement } from 'react';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';

import { isAllowedElement } from '../../helpers/is-allowed-element';
import { isFunction } from '../../helpers/is-function';
import { reduceObject } from '../../helpers/reduce-object';
import { prepareEventData } from '../../core/prepare-event-data';
import type { TEvent } from '../../core/models';

import type { TAnalyticsEventSender, TEventCallback } from './models';

const DEFAULT_TAG: TAnalyticsEventSender['$tag'] = 'span';

export const AnalyticsEventSender: FC<React.PropsWithChildren<TAnalyticsEventSender>> = ({
    $events,
    $tag,
    children,
    ...transitProps
}) => {
    if (!children) {
        console.info(
            'Используйте виджет AnalyticsEventSender.OnMount, чтобы отправить событие на монтирование.'
        );

        throw new Error('Виджет AnalyticsEventSender должен содержать дочерний элемент.');
    }

    const eventProps = reduceObject<TEvent, TEventCallback>(
        $events,
        (acc, [eventKey, eventValue]) => ({
            ...acc,
            [eventKey]: (...params) => {
                const eventData = prepareEventData(eventValue, params);
                sendEvent(eventData);

                if (
                    isAllowedElement(children) &&
                    isFunction<TEventCallback>(children.props[eventKey])
                ) {
                    children.props[eventKey](...params);
                }

                const transitEvent = transitProps[eventKey];
                if (isFunction<TEventCallback>(transitEvent)) transitEvent(...params);
            },
        })
    );

    /**
     * Если tag не прокинули, то проверяем что ребенок в children единственный и является валидным реакт компонентом,
     * а не string и boolean.
     */
    if (!$tag && isAllowedElement(children)) {
        return cloneElement(children, {
            ...transitProps,
            ...eventProps,
        });
    }

    /** Во всех остальных случаях рендерим, то что дали */
    const Tag = ($tag as keyof JSX.IntrinsicElements) || DEFAULT_TAG;

    return <Tag {...eventProps}>{children}</Tag>;
};
